import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { getActivityStatistics } from '@/api/statistical/statistical';
import { format } from 'date-fns';
import { MonthYearPicker } from '@/components/ui/month-year-picker';
import { useState, useEffect } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { Task } from '@/models/task.model';
import { Report } from '@/models/report.model';
import { Attendance } from '@/models/attendance';
import { IWfh } from '@/models/wfh.model';
import { WfhStatus } from '@/constants/wfh/wfh.constant';
import { useNavigate, useLocation } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

const ActivityStatistics: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [selectedDate, setSelectedDate] = useState<Date>(new Date());

    // Lấy uid từ URL path (ví dụ: /activity-statistics/6809ab93f3fb240c4c276481)
    const uid = location.pathname.split('/').pop();

    useEffect(() => {
        if (!uid) {
            navigate(-1);
        }
    }, [uid, navigate]);

    const { data, isLoading } = useQuery({
        queryKey: ['activityStatistics', selectedDate, uid],
        queryFn: () => getActivityStatistics({
            date: format(selectedDate, 'yyyy-MM'),
            uid: uid || ''
        }),
        enabled: !!uid
    });

    const calculateTaskStats = (tasks: Task[] = []) => {
        const activeTasks = tasks.filter(task => task.isActive).length;
        const inactiveTasks = tasks.filter(task => !task.isActive).length;
        return {
            total: tasks.length,
            active: activeTasks,
            inactive: inactiveTasks
        };
    };

    const calculateReportStats = (reports: Report[] = []) => {
        return {
            total: reports.length
        };
    };

    const calculateAttendanceStats = (checkIns: Attendance[] = []) => {
        const onTime = checkIns.filter(checkIn => !checkIn.isLate).length;
        const late = checkIns.filter(checkIn => checkIn.isLate).length;
        return {
            total: checkIns.length,
            onTime,
            late
        };
    };

    const calculateWfhStats = (wfh: IWfh[] = []) => {
        const approved = wfh.filter(item => item.status === WfhStatus.Approved).length;
        const pending = wfh.filter(item => item.status === WfhStatus.Pending).length;
        return {
            total: wfh.length,
            approved,
            pending
        };
    };

    if (isLoading) {
        return (
            <div className="space-y-4">
                <Skeleton className="h-8 w-[200px]" />
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                    {[...Array(4)].map((_, i) => (
                        <Skeleton key={i} className="h-[150px] w-full" />
                    ))}
                </div>
            </div>
        );
    }

    const taskStats = calculateTaskStats(data?.tasks);
    const reportStats = calculateReportStats(data?.reports);
    const attendanceStats = calculateAttendanceStats(data?.checkIns);
    const wfhStats = calculateWfhStats(data?.wfh);

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center space-x-4">
                    <Avatar className="h-12 w-12">
                        <AvatarImage src={data?.user.avatarUrl} />
                        <AvatarFallback>{data?.user.name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div>
                        <h1 className="text-2xl font-bold">{data?.user.name}</h1>
                        <p className="text-sm text-muted-foreground">{data?.user.email}</p>
                    </div>
                </div>
                <MonthYearPicker
                    label="Chọn tháng"
                    date={selectedDate}
                    onChange={(date) => date && setSelectedDate(date)}
                />
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <Card>
                    <CardHeader>
                        <CardTitle className="text-sm font-medium">Tasks</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{taskStats.total}</div>
                        <div className="text-xs text-muted-foreground">
                            Active: {taskStats.active} | Inactive: {taskStats.inactive}
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className="text-sm font-medium">Reports</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{reportStats.total}</div>
                        <div className="text-xs text-muted-foreground">
                            Total reports this month
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className="text-sm font-medium">Attendance</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{attendanceStats.total}</div>
                        <div className="text-xs text-muted-foreground">
                            On Time: {attendanceStats.onTime} | Late: {attendanceStats.late}
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className="text-sm font-medium">Remote Work</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{wfhStats.total}</div>
                        <div className="text-xs text-muted-foreground">
                            Approved: {wfhStats.approved} | Pending: {wfhStats.pending}
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default ActivityStatistics;